import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight, Package, Phone, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function Cart() {
  const { user } = useAuth();
  const { cart, updateQuantity, removeItem, clearCart, fetchCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => { if (user) fetchCart(); }, [user, fetchCart]);

  if (!user) { navigate('/login'); return null; }

  const items = cart.items || [];

  // ---- Volume-discount UX hints based on total pieces in cart ----
  // Mirrors the backend rules in routes/orders.calculate_discount():
  //   • 3 a 4 piezas  → 5 %
  //   • 5 a 10 piezas → 10 %
  //   • > 10 piezas   → invitar a contactar Atención de tienda para
  //                     un descuento especial negociado
  const totalQty = items.reduce((sum, it) => sum + (it.quantity || 0), 0);
  const tieneDescuentoEspecial = totalQty > 10;
  let tierActual = null;
  if (totalQty >= 5 && totalQty <= 10) tierActual = { label: '10% de descuento aplicado', pct: 10 };
  else if (totalQty >= 3) tierActual = { label: '5% de descuento aplicado', pct: 5 };

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-display font-semibold tracking-tight mb-8">Carrito de Compras</h1>

        {items.length === 0 ? (
          <div className="text-center py-16 space-y-4">
            <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mx-auto" />
            <p className="text-muted-foreground">Tu carrito está vacío</p>
            <Link to="/tienda"><Button>Explorar Tienda</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {items.map(item => {
                const prod = item.product;
                const img = prod?.images?.find(i => i.is_primary) || prod?.images?.[0];
                return (
                  <Card key={item.product_id}>
                    <CardContent className="p-4 flex gap-4">
                      <div className="w-20 h-20 bg-secondary/50 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {img ? <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full h-full object-cover" /> : <Package className="h-8 w-8 text-muted-foreground/30" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link to={`/tienda/${item.product_id}`} className="font-display font-semibold text-sm hover:text-[hsl(var(--ring))] transition-colors duration-150 line-clamp-1">{prod?.name}</Link>
                        <p className="font-mono text-xs text-muted-foreground">{prod?.sku}</p>
                        <p className="text-sm font-medium mt-1">{formatPrice(prod?.price || 0)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center border rounded-md">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}><Minus className="h-3 w-3" /></Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                        </div>
                        <p className="font-display font-semibold text-sm">{formatPrice((prod?.price || 0) * item.quantity)}</p>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => { removeItem(item.product_id); toast.success('Producto eliminado'); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              <Button variant="ghost" size="sm" onClick={() => { clearCart(); toast.success('Carrito vaciado'); }}>Vaciar carrito</Button>
            </div>

            <div>
              <Card className="sticky top-20">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-display font-semibold">Resumen</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatPrice(cart.total || 0)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Envío</span><span className="text-muted-foreground">Por calcular</span></div>
                  </div>

                  {/* Discount tier indicator (5% / 10%) — shown when cart qualifies */}
                  {tierActual && !tieneDescuentoEspecial && (
                    <div
                      className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center gap-2 text-emerald-700 dark:text-emerald-300"
                      data-testid="cart-discount-tier-badge"
                    >
                      <Sparkles className="h-4 w-4 flex-shrink-0" />
                      <span className="text-xs font-medium">{tierActual.label}</span>
                    </div>
                  )}

                  {/* Special-volume notice — more than 10 pieces */}
                  {tieneDescuentoEspecial && (
                    <div
                      className="rounded-md border border-[#295F80] bg-[#1B3345] px-4 py-3 space-y-2 text-white"
                      data-testid="cart-special-discount-notice"
                    >
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#536976]" />
                        <div className="space-y-1">
                          <p className="text-xs font-display font-semibold">
                            Descuento especial por volumen
                          </p>
                          <p className="text-xs text-gray-300 leading-relaxed">
                            Por compras de más de 10 piezas tenemos descuentos especiales negociados. Contacta a Atención de tienda para una cotización personalizada.
                          </p>
                        </div>
                      </div>
                      <a
                        href="tel:+525514541198"
                        className="flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors duration-150 px-3 py-2 text-xs font-medium"
                        data-testid="cart-special-discount-phone"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        +52 55 1454 1198 · Atención de tienda
                      </a>
                    </div>
                  )}

                  <div className="border-t pt-3 flex justify-between font-display font-semibold">
                    <span>Total</span>
                    <span>{formatPrice(cart.total || 0)}</span>
                  </div>
                  <Link to="/checkout">
                    <Button className="w-full gap-2" data-testid="cart-checkout-button">
                      Proceder al Checkout <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
